// @flow
import React, { PureComponent } from 'react';
import { withRouter } from 'react-router-dom';

import { withHelmet } from 'infrastructure/seo/Helmet';

import './EventListStyle.css';

import Breadcrumb from 'components/fragments/Breadcrumb';
import EventListPagination from 'components/event/EventListPagination';
import EventListNavbar from 'components/event/EventListNavbar';
import EventListRow from 'containers/event/EventListRowContainer';
import Loading from 'components/fragments/Loading';
import LiveScore from 'containers/fragments/LiveScoreContainer';

import { STATUS_LOADING, STATUS_FAILURE, STATUS_SUCCESS } from 'constants/statusConstants';
import { INITIAL_EVENTS_DISPLAYED } from 'constants/eventConstants';
import Button from 'components/fragments/button/Button';

import { groupBy } from 'utils/arrayUtils';
import { getPublicAssets } from 'utils/assetsUtils';
import { getJsDate, getMonthName, getMonthNumber } from 'utils/dateUtils';
import { getMetaBySlug } from 'utils/seoUtils';
import { parseHTMLEntity } from 'utils/documentsUtils';

import type { EventType, EventMatchType } from 'types/Event';
import type { Status } from 'types/Status';
import type { CompetitionEquipeType } from 'types/Settings';
import type { RouterProps } from 'types/Router';

export type StateProps = {
  events: EventType[],
  eventsStatus: Status,
  competitionsEquipes: CompetitionEquipeType,
  clubID: string,
  organization: string,
  featured_media: {
    src: string,
    legend: string
  },
  banniere: string,
  placeholder: string,
  rencontresLive: number,
  title: string
};

export type DispatchProps = {
  fetchEvents: (from: Date, to: Date, clubID: string, competitions: number[]) => void,
  fetchPageList: (slug: string) => void,
};

type Props = {
  match: {
    params: {
      month?: string,
      year?: string
    }
  },
  history: any,
  location: any
} & StateProps &
  DispatchProps &
  RouterProps;

type State = {
  currentMonth: Date,
  currentFilter: string,
  competitions: ?(number[]),
  clubFilterValues: string[],
  filteredEvents: ?(EventType[]),
  showAll: boolean,
  initialState: boolean
};

class EventList extends PureComponent<Props, State> {
  props: Props;
  state: State = {
    currentMonth: new Date(),
    currentFilter: 'allEvents',
    showAll: false,
    competitions: null,
    filteredEvents: null,
    clubFilterValues: [],
    initialState: true
  };

  componentDidMount() {
    const {
      fetchPageList,
      match: {
        params: { month, year }
      }
    } = this.props;

    const { pathname } = window.location;
    const slug = pathname.split('/')[1];

    fetchPageList(slug);

    let curMonth;
    if (month && year) {
      curMonth = getJsDate(month, year);

      if (isNaN(curMonth.getTime())) {
        curMonth = new Date();
      }
    } else {
      curMonth = new Date();
    }

    this.setState({ currentMonth: curMonth });
    this.updateEvents(curMonth);
  }

  static getDerivedStateFromProps(props: Props, state: State) {
    const { competitions } = state;
    const { competitionsEquipes } = props;

    if (!competitions && Object.keys(competitionsEquipes).length > 0) {
      const competitionsKeys = Object.keys(competitionsEquipes);
      const competitions = competitionsKeys.map(key => parseInt(key, 10));
      const clubFilterValues =
        competitions &&
        competitions.map((key: number): string => {
          const stringifiedKey = `${key}`;
          if (Object.prototype.hasOwnProperty.call(competitionsEquipes, stringifiedKey)) {
            return (competitionsEquipes[stringifiedKey] && parseHTMLEntity(competitionsEquipes[stringifiedKey].equipe_nom)) || '';
          }

          return '';
        });
      return { competitions, clubFilterValues };
    }
    return null;
  }

  componentDidUpdate(prevProps: Props, prevState: State) {
    const { competitions: prevCompState } = prevState;
    const {
      match: {
        params: { month, year }
      },
      clubID: prevClubId,
      events: prevEvents,
      location: prevLocation
    } = prevProps;
    const { clubID, competitionsEquipes, events, location } = this.props;
    const { currentMonth, competitions, initialState } = this.state;

    if (clubID !== prevClubId) {
      this.updateEvents(currentMonth);
    }

    // si on revient sur la page des évènements, le mois est le mois courant
    if (location.pathname === '/evenements' && location.pathname !== prevLocation.pathname) {
      let curMonth;

      if (month && year) {
        curMonth = getJsDate(month, year);

        if (isNaN(curMonth.getTime())) {
          curMonth = new Date();
        }
      } else {
        curMonth = new Date();
      }

      this.setState({ currentMonth: curMonth });
      this.updateEvents(curMonth);
    }

    if (prevCompState !== competitions && competitions && competitions.length > 0 && Object.keys(competitionsEquipes).length > 0) {
      this.updateEvents(currentMonth);
    }

    if (prevEvents.length !== events.length || initialState) {
      // au 1er rendu, on ne montre que les événements au maximum pour 3 journées
      // et on affiche un bouton voir plus lorsqu'il y a plus de 3 journées dans les events du mois
      const { currentMonth } = this.state;
      const eventsOfTheMonth = this.getEventsOfTheMonth(events, currentMonth);
      const groupedByStartDateEvents = this.getEventByStartDate(eventsOfTheMonth);
      // on récupère la liste des jours de départ
      const startDays = Object.keys(groupedByStartDateEvents);
      const showAll = startDays.length <= INITIAL_EVENTS_DISPLAYED;

      this.setState({ filteredEvents: eventsOfTheMonth, showAll, initialState: false });
    }
  }

  getEventByStartDate = eventsOfTheMonth => {
    // on trie les events par date
    const sortedEventsByStartDate = eventsOfTheMonth.sort((a, b) => (a.startDate < b.startDate ? -1 : a.startDate > b.startDate ? 1 : 0));

    // on souhaite regrouper les événements par date de début complète
    const groupedByStartDateEvents = groupBy(sortedEventsByStartDate, item => item.startDate.toString().slice(0, 10));

    return groupedByStartDateEvents;
  };

  updateEvents(date: Date) {
    const { fetchEvents, clubID } = this.props;

    const { competitions } = this.state;
    const currentYear = date.getFullYear();
    const currentMonth = date.getMonth();

    const firstDay = new Date(currentYear, currentMonth - 2, 1);
    const lastDay = new Date(currentYear, currentMonth + 3, 0);

    fetchEvents(firstDay, lastDay, clubID, competitions ? competitions : []);
  }

  updateCurrentMonth = (currentMonth: Date) => {
    this.updateEvents(currentMonth);
    this.setState({ currentMonth, currentFilter: 'allEvents', filteredEvents: null });
  };

  handleIncrementCurrentMonth = (e: MouseEvent) => {
    const { history } = this.props;
    const { currentMonth } = this.state;

    e.preventDefault();
    const date = currentMonth;
    date.setMonth(date.getMonth() + 1);
    const month = getMonthName(date.getMonth());
    history.push(`/evenements/${date.getFullYear()}/${encodeURIComponent(month.toLowerCase())}`);
    this.updateCurrentMonth(new Date(date));
  };

  handleDecrementCurrentMonth = (e: MouseEvent) => {
    e.preventDefault();
    const { history } = this.props;
    const { currentMonth } = this.state;

    const date = currentMonth;
    date.setMonth(date.getMonth() - 1);
    const month = getMonthName(date.getMonth());
    history.push(`/evenements/${date.getFullYear()}/${encodeURI(month.toLowerCase())}`);
    this.updateCurrentMonth(new Date(date));
  };

  handleCurrentMonthChanged = (date: string) => {
    const { history } = this.props;

    const currentMonth = new Date(date);
    const month = getMonthName(currentMonth.getMonth());
    history.push(`/evenements/${currentMonth.getFullYear()}/${encodeURI(month.toLowerCase())}`);
    this.updateCurrentMonth(currentMonth);
  };

  renderEmptyState = () => (
    <div className="placeholder ">
      <p className="ft-h5">
        <i className="icon icon-calendar-empty"></i>
        <br />
        Aucun évènement pour cette recherche
      </p>
    </div>
  );

  getFilteredEvents(filter: string, clubFilter, events: EventType[]) {
    return filter === 'allEvents'
      ? events
      : events.filter(event => {
          if (filter === 'Match') {
            return event.categories.some(({ name }) => name === filter) || Object.prototype.hasOwnProperty.call(event, 'compID');
          }

          if (filter === 'club') {
            return event.categories.some(({ name }) => clubFilter.includes(name));
          }

          return event.categories.some(({ name }) => name === filter);
        });
  }

  isValidSlug(slug?: string) {
    return slug !== null && slug !== undefined && slug !== '';
  }

  getDateFromSlugs(slug?: string, month?: string = '') {
    return this.isValidSlug(slug) && this.isValidSlug(month) ? new Date(parseInt(slug, 10), getMonthNumber(month), 1) : new Date();
  }

  getEventsOfTheMonth(events: EventType[], date: Date) {
    const eventsOfTheMonth = events.filter(event => {
      return (
        new Date(event.startDate).getMonth() === date.getMonth() ||
        (!event.compTag && new Date(event.endDate).getMonth() >= date.getMonth() && new Date(event.startDate).getMonth() <= date.getMonth())
      );
    });

    return eventsOfTheMonth;
  }

  sortEvents = (events: (EventType | EventMatchType)[]): any => {
    // const { showAll } = this.state;
    // const eventsToShow = showAll ? events.length : INITIAL_EVENTS_DISPLAYED;
    // const groupedEvents = groupBy(events, item => item.startDate.getDate());
    // const days = Object.keys(groupedEvents);

    // if (days.length) {
    //   return days
    //     .slice(0, eventsToShow)
    //     .map((ev, index) => <EventListRow key={index} events={[...groupedEvents[ev]]} />);
    // }
    const { competitionsEquipes } = this.props;
    const { currentFilter, currentMonth, showAll } = this.state;

    // on récupère les id des compétitions
    const competitions = Object.keys(competitionsEquipes);

    // on filtre les events selon allEvents, club ou Match
    const filterEvents = this.getFilteredEvents(
      currentFilter,
      competitions.map(id => parseInt(id, 10)),
      events
    );

    // const currentMonth = this.getDateFromSlugs(slug, month) ?? new Date().getMonth();
    const eventsOfTheMonth = this.getEventsOfTheMonth(filterEvents, currentMonth);

    // récupérer les events triés par date de départ
    const groupedByStartDateEvents = this.getEventByStartDate(eventsOfTheMonth);

    // on récupère la liste des jours de départ
    let startDays = Object.keys(groupedByStartDateEvents);

    // si on ne montre pas tous les events alors on filtre aux events des 3 1ères startDays
    startDays = showAll ? startDays : startDays.slice(0, 3);

    return startDays.length
      ? startDays.map(ev => {
          // si on n'a qu'un élément dans la date du jour, on rend directement l'événement dans une row
          if (groupedByStartDateEvents[ev].length === 1) {
            return <EventListRow key={ev} events={[...groupedByStartDateEvents[ev]]} />;
          }
          // sinon on va vouloir créer autant de row qu'il y a de couples date début / date de fin différents
          else {
            // on trie les events par date de fin
            const sortedEventsByEndDate = groupedByStartDateEvents[ev].sort((a, b) =>
              a.endDate < b.endDate ? -1 : a.endDate > b.endDate ? 1 : 0
            );

            // on souhaite regrouper les événements par date de fin complète
            const groupedByEndDateEvents = groupBy(sortedEventsByEndDate, item => item.endDate.toString().slice(0, 10));

            // on récupère la liste des jours de fin
            const endDays = Object.keys(groupedByEndDateEvents);

            return endDays.map(index => <EventListRow key={index} events={[...groupedByEndDateEvents[index]]} />);
          }
        })
      : this.renderEmptyState();
  };

  renderEvents = () => {
    const { events, eventsStatus } = this.props;
    const { competitions, filteredEvents } = this.state;

    if (eventsStatus === STATUS_FAILURE && events.length < 1) {
      return this.renderEmptyState();
    }

    if (eventsStatus === STATUS_SUCCESS || competitions === null) {
      return this.sortEvents(filteredEvents || events);
    }

    return <Loading isWhite />;
  };

  onFilterChange = (currentFilter: string) => {
    const { events } = this.props;
    const { clubFilterValues } = this.state;

    const filteredEvents = events.filter(event => {
      if (currentFilter === 'allEvents') {
        return events;
      }

      if (currentFilter === 'Match') {
        return event.categories.some(({ name }) => name === currentFilter) || Object.prototype.hasOwnProperty.call(event, 'compID');
      }

      if (currentFilter === 'club') {
        return event.categories.some(({ name }) => clubFilterValues.includes(name));
      }

      return event.categories.some(({ name }) => name === currentFilter);
    });
    const showAll = filteredEvents && filteredEvents.length < INITIAL_EVENTS_DISPLAYED;
    this.setState({ filteredEvents, currentFilter, showAll });
  };

  showMoreEvents = () => {
    this.setState({ showAll: true });
  };

  render() {
    const { currentMonth, currentFilter, showAll, competitions } = this.state;
    const {
      organization,
      events,
      banniere,
      placeholder,
      featured_media: { src },
      eventsStatus,
      rencontresLive,
      title
    } = this.props;
    if (eventsStatus === STATUS_LOADING && competitions === null) {
      return <Loading />;
    }

    const manual_filters = [];
    const filters = [{ label: 'Tous les évènements', value: 'allEvents' }];

    events.map(event => {
      if (Object.prototype.hasOwnProperty.call(event, 'compID')) {
        if (manual_filters.indexOf('Match') < 0) {
          manual_filters.push('Match');
          filters.push({ label: 'Match', value: 'Match' });
        }
      } else if (event.label) {
        if (manual_filters.indexOf(event.label) < 0) {
          manual_filters.push(event.label);
          filters.push({ label: event.label, value: event.label });
        }
      }
      return '';
    });

    let bannerClassName = 'banner banner--events';
    if (rencontresLive) {
      bannerClassName = 'banner banner--events banner--live-score';
    }
    return (
      <>
        <section className={bannerClassName}>
          <div className="banner__thumbnail img-blue-dark" style={{ backgroundImage: `url(${src || banniere || placeholder})` }} />
          <div className="container">
            <Breadcrumb elements={[{ name: 'accueil', to: '/' }, 'Évènements']} isDark />
            <h1 className="ft-h2">{title || `Les derniers évènements`} - {organization}</h1>
            <p className="banner__desc">Retrouvez l&apos;ensemble des évènements organisés par le club.</p>
          </div>
          <LiveScore />
        </section>
        <div className="container">
          <div className="row mb-6 mb-md-7 mb-lg-8">
            <div className="col">
              <div className="box">
                <div className={!showAll ? "events" : "events shown"}>
                  <EventListNavbar
                    filtersOptions={filters}
                    currentMonth={currentMonth}
                    onMonthChange={this.handleCurrentMonthChanged}
                    onFilterClick={this.onFilterChange}
                    selectedFilter={currentFilter}
                  />
                  {this.renderEvents()}
                  {!showAll && (
                    <Button
                      className="btn btn--see-more btn--primary"
                      href="#"
                      title="Voir plus d'évènements"
                      label="Voir plus d'évènements"
                      onClick={this.showMoreEvents}
                    />
                )}
                </div>
              
              <EventListPagination
                  currentMonth={this.state.currentMonth}
                  handleDecrementCurrentMonth={this.handleDecrementCurrentMonth}
                  handleIncrementCurrentMonth={this.handleIncrementCurrentMonth}
                />
                </div>
            </div>
          </div>
        </div>
      </>
    );
  }
}

export default withHelmet(withRouter(EventList))((Helmet, pageProps) => {
  const meta = getMetaBySlug('evenements');
  const {
    match: {
      params: { month, year }
    },
    organization
  } = pageProps;
  const description = meta.description(pageProps.ligue, month, year);
  const title = meta.title(organization, pageProps.ligue, month, year);

  return (
    <Helmet>
      <title>{title}</title>
      <meta name={'description'} content={description} />
      <meta name={'keywords'} content={`Évènements ${organization}`} />
      <meta property={'og:title'} content={title} />
      <meta property={'og:description'} content={description} />
      <meta property={'og:image'} content={getPublicAssets('/img/banner/gerer-mon-club.jpg')} />
      <meta property={'og:image:url'} content={getPublicAssets('/img/banner/gerer-mon-club.jpg')} />
      <link rel={'canonical'} href={window.location.href} />
    </Helmet>
  );
});
